import { Text, VStack } from '@chakra-ui/react';

import { formatValue } from 'react-currency-input-field';

import ProfileField from 'app/components/UI/ProfileField';
import ProfileSection from 'app/components/UI/ProfileSection';

import {
  NonBusinessDayFailover,
  PayCalendarLocation,
  PayCalendarWorkingDay,
  Payday,
  PayFrequency,
  PayrollCutoff,
  PayType,
  AccrualPeriodType,
  DeductionsConfigurations,
} from 'app/constants/payCalendar';
import { getOrdinalSuffix } from 'app/utils/date';

import { format, getDate } from 'date-fns';

import { DEFAULT_DATE_FORMAT } from '@aslanpay/portal-common-lib';

import { PayCalendarProfileTabProps } from './types';

function PayCalendarProfileTab({
  payCalendar,
  companyConfiguration,
  section,
}: PayCalendarProfileTabProps) {
  const isReserveEnabled = companyConfiguration?.modulesConfig.modules.RESERVE === 'ENABLED';
  const isOnDemandPayEnabled =
    companyConfiguration?.modulesConfig.modules.ON_DEMAND_PAY === 'ENABLED';

  return (
    <VStack spacing="40px" align="stretch" w="full">
      {section === 'general' && (
        <>
          <ProfileSection title="General details" maxW="full">
            <ProfileField label="Pay calendar ID" value={payCalendar?.friendlyId} />
            <ProfileField
              label="Pay calendar internal ID"
              value={payCalendar?.id}
              copyToClipboard
            />
            <ProfileField label="Pay calendar name" value={payCalendar?.name} />
            <ProfileField
              label="External pay calendar ID"
              value={payCalendar?.externalPayCalendarId}
            />
            <ProfileField
              label="Pay frequency"
              value={PayFrequency[payCalendar?.payFrequency || 'MONTHLY']}
            />
            <ProfileField
              label="Location (country)"
              value={PayCalendarLocation[payCalendar?.location || 'ENGLAND_WALES']}
            />
          </ProfileSection>

          <ProfileSection title="Payday information" maxW="full">
            <ProfileField
              label="How do you pay"
              value={PayType[payCalendar?.payType || 'IN_PERIOD']}
            />
            <ProfileField
              label="When does the pay calendar start"
              value={format(new Date(payCalendar?.startDate ?? '1970-01-01'), DEFAULT_DATE_FORMAT)}
            />
            <ProfileField
              label="When do you pay"
              value={Payday[payCalendar?.payday || 'LAST_DAY_OF_PERIOD']}
            />
            {payCalendar?.payday === 'FIXED_DAY' && payCalendar.paydayFixedDay && (
              <ProfileField
                label="The fixed day is"
                value={`${getDate(new Date(payCalendar.paydayFixedDay))}${getOrdinalSuffix(
                  getDate(new Date(payCalendar.paydayFixedDay))
                )} of each month`}
              />
            )}
            {payCalendar?.payday === 'LAST_CUSTOM_WEEKDAY' &&
              payCalendar.paydayLastCustomWeekday && (
                <ProfileField
                  label="Last week day"
                  value={PayCalendarWorkingDay[payCalendar.paydayLastCustomWeekday]}
                />
              )}
            {(payCalendar?.payday === 'N_DAY_OF_PERIOD' ||
              payCalendar?.payday === 'N_BUSINESS_DAY_OF_PERIOD' ||
              payCalendar?.payday === 'CUSTOM_DAY' ||
              payCalendar?.payday === 'CUSTOM_DAY_IN_ARREARS') &&
              payCalendar.paydayCustomDay && (
                <ProfileField
                  label={
                    payCalendar?.payday === 'N_DAY_OF_PERIOD' ||
                    payCalendar?.payday === 'N_BUSINESS_DAY_OF_PERIOD'
                      ? 'Nth day'
                      : 'Custom day'
                  }
                  value={payCalendar.paydayCustomDay}
                />
              )}
            <ProfileField
              label="Default payday action"
              value={
                NonBusinessDayFailover[payCalendar?.paydayNonBusinessDayFailover ?? 'NO_CHANGE']
              }
            />
          </ProfileSection>

          <ProfileSection
            title="Cut-off details"
            maxW="full"
            tooltip="It’s a date and time programmed to ensure that the employer's payroll team can accurately process pay during this period."
          >
            <ProfileField
              label="Cut-off type"
              value={PayrollCutoff[payCalendar?.payrollCutoff || 'N_DAYS_BEFORE_PAYDAY']}
            />
            {payCalendar?.payrollCutoff === 'FIXED_DAY' && payCalendar.payrollCutoffFixedDay && (
              <ProfileField
                label="The fixed day is"
                value={`${getDate(new Date(payCalendar.payrollCutoffFixedDay))}${getOrdinalSuffix(
                  getDate(new Date(payCalendar.payrollCutoffFixedDay))
                )} of each month`}
              />
            )}
            {(payCalendar?.payrollCutoff !== 'FIXED_DAY' &&
              payCalendar?.payrollCutoff === 'CUSTOM_DAY') ||
            payCalendar?.payrollCutoff === 'CUSTOM_DAY_IN_ARREARS' ? (
              <ProfileField label="Custom day" value={payCalendar?.payrollCutoffCustomDay} />
            ) : (
              payCalendar?.payrollCutoff !== 'FIXED_DAY' && (
                <ProfileField
                  label="Cut-off number of days"
                  value={
                    payCalendar?.payrollCutoffDays === 0 ? '0' : payCalendar?.payrollCutoffDays
                  }
                />
              )
            )}
            <ProfileField
              label="Default cut-off action"
              value={
                NonBusinessDayFailover[payCalendar?.cutoffNonBusinessDayFailover ?? 'NO_CHANGE']
              }
            />
          </ProfileSection>

          <ProfileSection title="Working pattern" maxW="full">
            <ProfileField
              label="Working days"
              value={payCalendar?.workingDays.map((day) => PayCalendarWorkingDay[day]).join(', ')}
            />
            <ProfileField
              label="Default salary amount (optional)"
              value={formatValue({
                value: payCalendar?.defaultAnnualGrossSalary?.toString(),
                prefix: '£',
                groupSeparator: ',',
                decimalSeparator: '.',
                decimalScale: 2,
              })}
            />
          </ProfileSection>
        </>
      )}

      {section === 'configuration' && (
        <>
          <ProfileSection title="Dynamic Allowances" maxW="full">
            {isReserveEnabled && (
              <ProfileField
                label="Global limit (%)"
                value={`${payCalendar?.globalAllowancePercentage || 0}%`}
              />
            )}
            {isOnDemandPayEnabled && (
              <ProfileField
                label="On-demand pay sub-limit (%)"
                value={`${payCalendar?.odpAllowancePercentage}%`}
              />
            )}
          </ProfileSection>

          <ProfileSection title="ODP Configuration" maxW="full">
            <ProfileField
              label="Accrual period"
              value={(() => {
                if (!payCalendar?.accrualPeriod) return '-';
                return (
                  AccrualPeriodType[payCalendar.accrualPeriod as keyof typeof AccrualPeriodType] ||
                  '-'
                );
              })()}
            />
            <ProfileField
              label="1st of the month accrual (working days)"
              value={`${
                payCalendar?.startingBalanceDays === 0 &&
                payCalendar.fullPeriodStartingBalance === true
                  ? 'Whole period - all workings days'
                  : payCalendar?.startingBalanceDays
              }`}
            />
          </ProfileSection>

          <ProfileSection title="Set and Spend Configuration" maxW="full">
            <Text textStyle="h6SemiBold" color="brand.darkPurple">
              Made Available Time (MAT)
            </Text>
            <ProfileField
              label="Salaried workers"
              value={(() => {
                if (!payCalendar?.salariedDeductionsRequestMadeAvailableConfig) return '-';
                return (
                  DeductionsConfigurations[
                    payCalendar.salariedDeductionsRequestMadeAvailableConfig as keyof typeof DeductionsConfigurations
                  ] || '-'
                );
              })()}
            />
            <ProfileField
              label="Non-salaried workers"
              value={(() => {
                if (!payCalendar?.nonSalariedDeductionsRequestMadeAvailableConfig) return '-';
                return (
                  DeductionsConfigurations[
                    payCalendar.nonSalariedDeductionsRequestMadeAvailableConfig as keyof typeof DeductionsConfigurations
                  ] || '-'
                );
              })()}
            />

            <Text textStyle="h6SemiBold" color="brand.darkPurple" mt="10px">
              Get Paid Early
            </Text>
            <ProfileField
              label="Salaried"
              value={payCalendar?.salariedGpeEnabled ? 'Enabled' : 'Disabled'}
            />
            <ProfileField
              label="Salaried days before payday cap"
              value={
                payCalendar?.salariedGpeDaysBeforePaydayCap != null
                  ? `${payCalendar.salariedGpeDaysBeforePaydayCap} days`
                  : '-'
              }
            />
            <ProfileField
              label="Non-salaried"
              value={payCalendar?.nonSalariedGpeEnabled ? 'Enabled' : 'Disabled'}
            />
            <ProfileField
              label="Non-salaried days before payday cap"
              value={
                payCalendar?.nonSalariedGpeDaysBeforePaydayCap != null
                  ? `${payCalendar.nonSalariedGpeDaysBeforePaydayCap} days`
                  : '-'
              }
            />
          </ProfileSection>
        </>
      )}
    </VStack>
  );
}

export default PayCalendarProfileTab;
